import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Box, styled } from '@mui/material'
import { useGetBasicInfo } from '@/hooks/api/dashboard/useGetBasicInfo'
import { useGetTopics } from '@/hooks/api/dashboard/useGetTopics'
import { useConnectionAdd } from '@/hooks/api/dashboard/useConnectionAdd'
import { useConnectionAccept } from '@/hooks/api/dashboard/useConnectionAccept'
import { useConnectionDecline } from '@/hooks/api/dashboard/useConnectionDecline'
import { useConnectionsListViewModel } from '@/hooks/api/dashboard/viewModels/useConnectionsListViewModel'
import { useLocalStorage } from '@/hooks/common'
import { TopicType } from '@/utils/constants'
import { SearchType, Topic } from '@/types'
import { topicCoverUrl } from '@/features/album/mappers'
import { getClassicVariant } from '@/features/gallery-studio-3d/classicCatalog'
import { isValidAvatarUrl } from '@/utils/helper'
import { ProfileHeroWall } from '../../components/ProfileHeroWall/ProfileHeroWall'
import { ProfileSubNav, ProfileSection } from '../../components/ProfileSubNav/ProfileSubNav'
import { ProfileSectionHeader } from '../../components/ProfileSectionHeader/ProfileSectionHeader'
import { GallerySpotlight } from '../../components/GallerySpotlight/GallerySpotlight'
import { GalleryRail } from '../../components/GalleryRail/GalleryRail'
import { ConnectionsPreviewGrid } from '../../components/ConnectionsPreviewGrid/ConnectionsPreviewGrid'
import { ProfileBioCard } from '../../components/ProfileBioCard/ProfileBioCard'
import { ActivityPulseCard } from '../../components/ActivityPulseCard/ActivityPulseCard'
import { deriveActivityItems } from '../../utils/deriveActivity'
import { ConnectState } from '../../components/ProfileHeroActions/ProfileHeroActions'

/** Gallery list items reuse profilePicThumbURL for the owner — never treat that as room art. */
function galleryCoverUrl(topic: Topic, albumCoverById: Record<string, string>): string | undefined {
  const fromPhotos = topicCoverUrl(topic)
  if (fromPhotos) return fromPhotos
  try {
    if (topic.topicXData) {
      const parsed = JSON.parse(topic.topicXData)
      const albumId = parsed?.sourceAlbumId != null ? String(parsed.sourceAlbumId) : undefined
      if (albumId && albumCoverById[albumId]) return albumCoverById[albumId]
      if (parsed?.wizard?.sceneId != null) {
        const classic = getClassicVariant(Number(parsed.wizard.sceneId))
        if (classic?.thumbUrl) return classic.thumbUrl
      }
    }
  } catch {
    /* ignore malformed xdata */
  }
  return undefined
}

function pickAvatarUrl(info: {
  profilePicThumbURL?: string
  profilePicURL?: string
  profilePicFullURL?: string
} | null): string | undefined {
  if (!info) return undefined
  return [info.profilePicThumbURL, info.profilePicURL, info.profilePicFullURL].find(
    (u): u is string => Boolean(u && isValidAvatarUrl(u)),
  )
}

const Section = styled(Box)({
  padding: '30px 28px 0',
  scrollMarginTop: 104,
})

const AboutGrid = styled(Box, {
  shouldForwardProp: (p) => p !== '$twoCol',
})<{ $twoCol: boolean }>(({ $twoCol }) => ({
  display: 'grid',
  gridTemplateColumns: $twoCol ? '1.3fr 1fr' : '1fr',
  gap: 20,
  alignItems: 'start',
  '@media (max-width: 900px)': { gridTemplateColumns: '1fr' },
}))

function deriveConnectState(info: {
  isConnection: boolean
  reqSent: boolean
  reqReceived: boolean
}): ConnectState {
  if (info.isConnection) return 'connected'
  if (info.reqSent) return 'sent'
  if (info.reqReceived) return 'received'
  return 'none'
}

function parseJoinYear(raw?: string) {
  if (!raw) return undefined
  const d = new Date(raw)
  if (isNaN(d.getTime())) return undefined
  return d.getFullYear().toString()
}

export function UserProfileScreen() {
  const { userId = 'me' } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const [ewUser] = useLocalStorage('ewUser', null) as [{ userID: string } | null, unknown]

  const resolvedId = userId === 'me' ? (ewUser?.userID ?? '') : (userId ?? '')
  const targetId = resolvedId
  const isSelf = ewUser?.userID === targetId || userId === 'me'

  const getBasicInfo = useGetBasicInfo(targetId)
  const getTopics = useGetTopics()
  const addConnection = useConnectionAdd()
  const acceptConnection = useConnectionAccept()
  const declineConnection = useConnectionDecline()
  const connListVm = useConnectionsListViewModel()

  const [basicInfo, setBasicInfo] = useState<Awaited<ReturnType<typeof getBasicInfo.mutateAsync>> | null>(null)
  const [galleries, setGalleries] = useState<Topic[]>([])
  const [albums, setAlbums] = useState<Topic[]>([])
  const [actionLoading, setActionLoading] = useState(false)
  const [activeSection, setActiveSection] = useState<ProfileSection>('overview')

  const overviewRef = useRef<HTMLElement>(null)
  const galleriesRef = useRef<HTMLElement>(null)
  const connectionsRef = useRef<HTMLElement>(null)
  const aboutRef = useRef<HTMLElement>(null)

  useEffect(() => {
    if (!targetId) return

    const searchType = isSelf ? SearchType.loggedInUser : SearchType.OtherUser
    const listParams = {
      pageNo: 1,
      interaction: 3,
      searchType,
      ...(isSelf ? {} : { connID: targetId }),
    }

    getBasicInfo.mutateAsync({ isUserProfile: true }).then((data) => {
      setBasicInfo(data)
    })

    Promise.all([
      getTopics.mutateAsync({ ...listParams, topicType: TopicType.GALLERY }),
      getTopics.mutateAsync({ ...listParams, topicType: TopicType.ALBUM }),
    ]).then(([galleryTopics, albumTopics]) => {
      setGalleries(galleryTopics || [])
      setAlbums(albumTopics || [])
    })

    connListVm.getConnectionsList({ connID: targetId })
  }, [targetId, isSelf])

  const connectState: ConnectState = basicInfo
    ? deriveConnectState({
        isConnection: basicInfo.isConnection,
        reqSent: basicInfo.reqSent,
        reqReceived: basicInfo.reqReceived,
      })
    : 'none'

  const handleConnect = useCallback(async () => {
    if (!targetId) return
    setActionLoading(true)
    try {
      await addConnection.mutateAsync({ connID: targetId, cliques: [] })
      setBasicInfo((prev) => (prev ? { ...prev, reqSent: true } : prev))
    } finally {
      setActionLoading(false)
    }
  }, [targetId])

  const handleAccept = useCallback(async () => {
    if (!targetId || !basicInfo?.reqReceivedConnReqID) return
    setActionLoading(true)
    try {
      await acceptConnection.mutateAsync({ connReqID: String(basicInfo.reqReceivedConnReqID), cliques: [] })
      setBasicInfo((prev) => (prev ? { ...prev, isConnection: true, reqReceived: false } : prev))
    } finally {
      setActionLoading(false)
    }
  }, [targetId, basicInfo])

  const handleDecline = useCallback(async () => {
    if (!targetId || !basicInfo?.reqReceivedConnReqID) return
    setActionLoading(true)
    try {
      await declineConnection.mutateAsync(String(basicInfo.reqReceivedConnReqID))
      setBasicInfo((prev) => (prev ? { ...prev, reqReceived: false } : prev))
    } finally {
      setActionLoading(false)
    }
  }, [targetId, basicInfo])

  const handlePersonClick = useCallback((conn: { userID: string }) => {
    navigate(`/profile/${conn.userID}`)
  }, [navigate])

  // A wall's galleries belong to that profile's owner — on your own profile
  // that's you, so the editor is right. On anyone else's, route straight to
  // the real read-only tour instead of the editor Studio3DScreen would
  // otherwise redirect away from anyway.
  const handleGalleryOpen = useCallback(
    (topic: Topic) => {
      navigate(isSelf ? `/galleries/${topic.topicID}/studio` : `/g/${topic.topicID}`)
    },
    [navigate, isSelf],
  )

  const scrollTo = (ref: React.RefObject<HTMLElement | null>, section: ProfileSection) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    setActiveSection(section)
  }

  const isLoading = getBasicInfo.isPending

  const spotlightGallery = galleries[0] ?? undefined
  const railGalleries = galleries.slice(1)

  const galleryCount = galleries.length
  const albumCount = albums.length
  const connCount = parseInt(basicInfo?.connCount ?? '0', 10) || 0

  const albumCoverById = useMemo(() => {
    const map: Record<string, string> = {}
    for (const a of albums) {
      const cover = topicCoverUrl(a)
      if (cover) map[a.topicID] = cover
    }
    return map
  }, [albums])

  const galleryCoverById = useMemo(() => {
    const map: Record<string, string | undefined> = {}
    for (const g of galleries) {
      map[g.topicID] = galleryCoverUrl(g, albumCoverById)
    }
    return map
  }, [galleries, albumCoverById])

  const mosaicImages = useMemo(() => {
    const fromGalleries = galleries
      .map((g) => galleryCoverById[g.topicID])
      .filter((src): src is string => Boolean(src))
    const fromAlbums = albums
      .map((a) => albumCoverById[a.topicID])
      .filter((src): src is string => Boolean(src))
    const profileCover = basicInfo?.profileCoverThumbURL || basicInfo?.profileCoverURL || undefined
    const merged = [...fromGalleries, ...fromAlbums]
    if (profileCover) merged.unshift(profileCover)
    return Array.from(new Set(merged))
  }, [galleries, albums, galleryCoverById, albumCoverById, basicInfo])

  const avatarUrl = pickAvatarUrl(basicInfo)

  const connectionsVisible = isSelf || basicInfo?.connListVisible !== false
  const visibleConnections = connListVm.connections?.slice(0, 8) ?? []
  const activityItems = useMemo(() => deriveActivityItems(galleries, albums), [galleries, albums])
  const seeAllConnections = () =>
    isSelf ? navigate('/connections') : navigate(`/profile/${targetId}/connections`)

  // Reject values that look like email addresses — the API sometimes stores
  // an email in the userName field before the user sets a real handle.
  const handle = basicInfo?.userName && !basicInfo.userName.includes('@')
    ? basicInfo.userName
    : undefined

  return (
    <Box>
      <Box component='section' ref={overviewRef} data-section='overview' sx={{ mx: '28px', mt: '12px' }}>
        <ProfileHeroWall
          loading={isLoading}
          firstName={basicInfo?.firstName ?? ''}
          lastName={basicInfo?.lastName ?? ''}
          userName={handle}
          profession={basicInfo?.profession}
          location={basicInfo?.location}
          motto={basicInfo?.motto}
          joinYear={parseJoinYear(basicInfo?.joinDate)}
          avatarUrl={avatarUrl}
          mosaicImages={mosaicImages}
          isPro={basicInfo?.subscriptionStatus === 'active'}
          galleries={galleryCount}
          albums={albumCount}
          connections={connCount}
          isSelf={isSelf}
          connectState={connectState}
          onBack={isSelf ? undefined : () => navigate('/connections')}
          onConnect={handleConnect}
          onAccept={handleAccept}
          onDecline={handleDecline}
          onEditProfile={() => navigate('/settings')}
          onViewConnections={seeAllConnections}
          onGalleriesStatClick={() => scrollTo(galleriesRef, 'galleries')}
          onConnectionsStatClick={seeAllConnections}
          actionLoading={actionLoading}
        />
      </Box>

      <ProfileSubNav
        active={activeSection}
        onChange={setActiveSection}
        overviewRef={overviewRef}
        galleriesRef={galleriesRef}
        connectionsRef={connectionsRef}
        aboutRef={aboutRef}
      />

      <Box component='section' ref={galleriesRef} data-section='galleries'>
        <Section>
          <ProfileSectionHeader
            title={isSelf ? 'Your wall' : 'The wall'}
            aside={`${galleryCount} GALLERIES`}
          />
          <GallerySpotlight
            topic={spotlightGallery}
            coverUrl={spotlightGallery ? galleryCoverById[spotlightGallery.topicID] : undefined}
            loading={getTopics.isPending}
            isSelf={isSelf}
            onClick={handleGalleryOpen}
            onEmptyAction={isSelf ? () => navigate('/galleries') : undefined}
          />
          {railGalleries.length > 0 && (
            <>
              <ProfileSectionHeader
                title={isSelf ? 'More from you' : `More from ${basicInfo?.firstName ?? 'them'}`}
                aside='SCROLL →'
                sx={{ marginTop: '26px' }}
              />
              <GalleryRail
                topics={railGalleries}
                coverById={galleryCoverById}
                loading={getTopics.isPending}
                onOpen={handleGalleryOpen}
              />
            </>
          )}
        </Section>
      </Box>

      {connectionsVisible && (
        <Box component='section' ref={connectionsRef} data-section='connections'>
          <Section>
            <ProfileSectionHeader
              title={isSelf ? 'People in your circle' : 'People in their circle'}
              aside={`${connCount} CONNECTIONS`}
              onSeeAll={seeAllConnections}
            />
            <ConnectionsPreviewGrid
              connections={visibleConnections}
              loading={connListVm.loading}
              onPersonClick={handlePersonClick}
            />
          </Section>
        </Box>
      )}

      <Box component='section' ref={aboutRef} data-section='about' sx={{ pb: '10px' }}>
        <Section>
          <ProfileSectionHeader title='About' />
          <AboutGrid $twoCol={activityItems.length > 0}>
            <ProfileBioCard
              loading={isLoading}
              motto={basicInfo?.motto}
              profession={basicInfo?.profession}
              location={basicInfo?.location}
              joinDate={basicInfo?.joinDate}
            />
            <ActivityPulseCard items={activityItems} />
          </AboutGrid>
        </Section>
      </Box>
    </Box>
  )
}
